/**
 * General adapter definition and answer helpers. Workflow (blocks, steps, resources), file processing route
 * and release lookup helpers live in their own modules and are re-exported here.
 */
import type { ReleaseAdapterState, ReleaseMapping } from "@packages/commons";
import { mergeAnswers, readAnswer, matchesAdapterAnswers } from "./answers";
import { getStepBlocksOfKind, getWorkflowResources, MAPPING_RESOURCE_KINDS, isStepApplicable } from "./workflow";
import type {
  AdapterApplyAnswers,
  AdapterDefinition,
  AdapterEntitySelection,
  AdapterTriggerDefinition,
  AdapterMappingRequiredEdit,
  AdapterMappingDefinition,
  AdapterQuestion,
  AdapterQuestionOption,
  AdapterWorkflowStepDefinition,
  ReleaseWithAdapterCollections,
} from "./types";

export const defaultAdapterAnswers: AdapterApplyAnswers = {
  authType: "none",
};

export const getAdapterPersistenceId = (definitionId: string): string =>
  `${definitionId}-integration`;

export const getAdapterRecord = (
  release: ReleaseWithAdapterCollections,
  definition: AdapterDefinition,
): ReleaseAdapterState | undefined => release.adapters?.find((adapter) => adapter.format === 2 && adapter.id === getAdapterPersistenceId(definition.id));

export const getAnswers = (
  release: ReleaseWithAdapterCollections,
  definition: AdapterDefinition,
  overrides?: Partial<AdapterApplyAnswers>,
): AdapterApplyAnswers =>
  mergeAnswers<AdapterApplyAnswers>(
    definition.defaultAnswers,
    getAdapterRecord(release, definition)?.answers as Partial<AdapterApplyAnswers> | undefined,
    overrides,
  );

/** An `entities` block's entities as `entity` questions, so answers, completion and tagging treat them alike. */
export const getEntitySelectionQuestions = (block: { entities: AdapterEntitySelection[] }): AdapterQuestion[] =>
  block.entities.map((entity) => ({ ...entity, type: "entity" as const }));

/** The questions a step asks, from its `questions` and `entities` blocks. */
export const getStepQuestions = (step: Pick<AdapterWorkflowStepDefinition, "blocks">): AdapterQuestion[] => [
  ...getStepBlocksOfKind(step, "questions").flatMap((block) => block.questions),
  ...getStepBlocksOfKind(step, "entities").flatMap(getEntitySelectionQuestions),
];

export const getAdapterQuestions = (definition: AdapterDefinition): AdapterQuestion[] =>
  definition.workflow.flatMap(getStepQuestions);

export const getApplicableQuestions = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): AdapterQuestion[] => getAdapterQuestions(definition).filter((question) => matchesAdapterAnswers(answers, question.showWhen));

export const areAdapterQuestionsAnswered = (
  questions: AdapterQuestion[],
  answers: AdapterApplyAnswers,
): boolean => questions.filter((question) => question.required).every((question) => {
  const value = readAnswer(answers, question.key);
  return value !== undefined && value !== null && (typeof value !== "string" || value.trim().length > 0);
});

/** The option currently chosen for the definition's naming question, if it declares one. */
export const getAdapterNameOption = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): AdapterQuestionOption | undefined => {
  if (!definition.nameQuestionKey) return undefined;
  const chosen = readAnswer(answers, definition.nameQuestionKey);
  return getAdapterQuestions(definition)
    .find((question) => question.key === definition.nameQuestionKey)
    ?.options?.find((option) => option.value === chosen);
};

export const getAdapterDisplayName = (definition: AdapterDefinition, answers: AdapterApplyAnswers): string => {
  const label = getAdapterNameOption(definition, answers)?.label;
  return label ? `${definition.name} - ${label}` : definition.name;
};

export const getApplicableMappings = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): AdapterMappingDefinition[] =>
  getWorkflowResources(definition, MAPPING_RESOURCE_KINDS)
    .filter(({ step }) => isStepApplicable(step, answers))
    .map(({ declaration }) => declaration.resource);

export const isAdapterConnectionRequired = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): boolean => getWorkflowResources(definition, ["connection"]).some(({ step }) => isStepApplicable(step, answers));

export const getApplicableDeliveries = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
): AdapterTriggerDefinition[] =>
  getWorkflowResources(definition, ["delivery"])
    .filter(({ step }) => isStepApplicable(step, answers))
    .map(({ declaration }) => declaration.resource);

/** The attribute the user chose for a delivery, from the question the delivery names. */
export const getDeliveryTarget = (
  answers: AdapterApplyAnswers,
  delivery: Pick<AdapterTriggerDefinition, "targetQuestionKey">,
): string | undefined => {
  const value = readAnswer(answers, delivery.targetQuestionKey);
  return typeof value === "string" && value.trim() ? value : undefined;
};

const pathSegmentPattern = /([^[.\]]+)|\[(\d+)\]/g;

const getPathValue = (source: unknown, path: string): unknown => {
  const segments = Array.from(path.matchAll(pathSegmentPattern)).map((match) => match[1] ?? match[2]);
  let current = source;

  for (const segment of segments) {
    if (Array.isArray(current)) {
      current = current[Number(segment)];
    } else if (current && typeof current === "object") {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }

  return current;
};

const isBlankValue = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === "string" && value.trim().length === 0);

export const getUnresolvedMappingRequiredEdits = (
  mapping: ReleaseMapping,
  requiredEdits: AdapterMappingRequiredEdit[] | undefined,
): AdapterMappingRequiredEdit[] => {
  if (!requiredEdits?.length) return [];

  return requiredEdits.filter((edit) => {
    const value = getPathValue(mapping, edit.path);
    if (!isBlankValue(value)) return false;

    const nestedValue = getPathValue((mapping as unknown as Record<string, unknown>).data, edit.path);
    return isBlankValue(nestedValue);
  });
};

export const getAdapterSessionGoal = (definition: AdapterDefinition, answers: AdapterApplyAnswers): string | undefined => {
  const goals = [...new Set(getApplicableDeliveries(definition, answers)
    .filter((delivery) => delivery.sessionGoal)
    .map((delivery) => getDeliveryTarget(answers, delivery)).filter(Boolean))];
  if (goals.length > 1) throw new Error("File processing can use only one session goal.");
  return goals[0];
};

export { matchesAdapterAnswers } from "./answers";
export * from "./workflow";
export * from "./fileProcessingRoutes";
export * from "./releaseLookup";
